import React from 'react'
import CommonButtons from 'src/shared/components/CommonButtons'
import { CreditLimitRequest } from './types'
import CreditRequestStatusPill from './CreditRequestStatusPill'

const QUICK_ADD_AMOUNTS = [100, 500, 1000]

interface CreditLimitControlProps {
    currency: string
    draftLimit: string
    creditLimit: number
    request: CreditLimitRequest | null
    isSubmitting: boolean
    validationError: string | null
    canManageCreditLimit: boolean
    onDraftChange: (value: string) => void
    onBump: (amount: number) => void
    onSubmit: () => void
    fmt: (n: number) => string
}

export default function CreditLimitControl({
    currency,
    draftLimit,
    creditLimit,
    request,
    isSubmitting,
    validationError,
    canManageCreditLimit,
    onDraftChange,
    onBump,
    onSubmit,
    fmt,
}: CreditLimitControlProps) {
    const parsedDraft = parseFloat(draftLimit)
    const hasChange = !isNaN(parsedDraft) && parsedDraft !== creditLimit

    const buttonLabel = isSubmitting
        ? (canManageCreditLimit ? 'Updating…' : 'Requesting…')
        : (canManageCreditLimit ? 'Update' : 'Request')

    return (
        <div className="mt-5 border-t pt-4 space-y-3">
            <div className="flex items-baseline justify-between">
                <p className="text-[12.5px] font-semibold text-slate-900">
                    {canManageCreditLimit ? 'Change credit limit' : 'Request a credit limit increase'}
                </p>
                <span className="text-[11px] text-slate-400">Current: {fmt(creditLimit)}</span>
            </div>

            <div className="flex items-center gap-2">
                <input
                    id="creditLimitInput"
                    type="text"
                    inputMode="decimal"
                    value={draftLimit}
                    onChange={(e) => onDraftChange(e.target.value)}
                    className="border rounded-md px-2.5 py-1.5 w-32 text-right text-[13px] focus:outline-none focus:ring-2 focus:ring-indigo-300"
                    placeholder="0.00"
                    disabled={isSubmitting}
                />
                <span className="text-[12px] text-slate-500">{currency}</span>
                <CommonButtons
                    label={buttonLabel}
                    variant="primary"
                    onClick={onSubmit}
                    className="min-w-[90px] ml-auto"
                    disabled={isSubmitting || !hasChange}
                />
            </div>

            <div className="flex items-center gap-1.5">
                <span className="text-[11px] text-slate-400 mr-0.5">Quick add:</span>
                {QUICK_ADD_AMOUNTS.map((amount) => (
                    <button
                        key={amount}
                        type="button"
                        onClick={() => onBump(amount)}
                        disabled={isSubmitting}
                        className="text-[11.5px] font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-40 rounded-md px-2.5 py-1 transition-colors"
                    >
                        +{amount.toLocaleString()}
                    </button>
                ))}
                <span className="text-[11px] text-slate-400 ml-1">or type a custom amount above</span>
            </div>

            {validationError && <p className="text-red-600 text-[11.5px]">{validationError}</p>}
            {request && <CreditRequestStatusPill request={request} fmt={fmt} />}
        </div>
    )
}